// Matching photo print: a card under each eligible canvas line in the cart and the cart popup.
// Its own offer, independent of the copy offer (owner decision 2026-10-10): both can show.
// The Worker tells the canvas line's identity and print size; the print line carries that
// identity, so at payment the print is linked to exactly this canvas line.
// Plan: docs/plans/BAY-PHOTO-PRINT-ADDON.md, section 2.
import {requestOfferJson, cartOfferMutationLock, observeOfferExposure, announceOffer} from './mga-cart-offers.js';
const WORKER_URL = 'https://mga-standalone-dev.muokkis.workers.dev';
const SELECTOR = '[data-mga-print-offer]';
const OFFER_ID = 'matching_photo_print_v1';
const SHOP = 'bihgbd-ts.myshopify.com';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TOKEN = /^(?:\d{10,16}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)\.[a-f0-9]{64}$/i;
const PARENT_ID = /^cpi_[a-f0-9]{64}$/;
const VARIANT = /^gid:\/\/shopify\/ProductVariant\/([1-9]\d{0,19})$/;
// Print size by artwork shape. Keep equal to shared/addon-print-shapes.js.
export const PRINT_SHAPES = Object.freeze({'2:3': '4x6', '1:1': '5x5', '2:1': '4x8'});
// Copies of one print a customer can take (owner decision 2026-10-14). Keep equal to
// ADDON_PRINT_MAX_QUANTITY in shared/addon-prints.js; registration refuses more.
export const MAX_PRINT_QUANTITY = 10;
export const validPrintQuantity = value => Number.isInteger(value) && value >= 1 && value <= MAX_PRINT_QUANTITY;

export function isPrintLine(line) {
  return String(line?.sku || '').toUpperCase().startsWith('PRT-') || line?.properties?._mga_addon_offer_id === OFFER_ID;
}

export function printConfiguration(value) {
  const print = value?.matchingPhotoPrint;
  if (value?.version !== 1 || value.profile !== 'mga' || value.shopDomain !== SHOP || value.availability?.matching_photo_print !== true
    || print?.offerId !== OFFER_ID || print.version !== '1' || print.currency !== 'USD' || print.country !== 'US'
    || !print.sizes || typeof print.sizes !== 'object' || Array.isArray(print.sizes)) return null;
  const sizes = Object.entries(print.sizes);
  return sizes.length > 0 && sizes.every(([size, entry]) => Object.values(PRINT_SHAPES).includes(size) && VARIANT.test(entry?.variantId || ''))
    ? value : null;
}

/** A personalized physical piece that a print can belong to. */
export function canvasLine(line) {
  const props = line?.properties;
  return !!props && typeof props === 'object' && !Array.isArray(props) && line.requires_shipping === true
    && Number.isInteger(line.quantity) && line.quantity >= 1 && !isPrintLine(line) && !props._mga_addon_kind
    && !line.selling_plan_allocation && !line.parent_relationship && !line.has_components
    && !String(line.sku || '').toUpperCase().startsWith('DIG-')
    && typeof props._mga_design_id === 'string' && props._mga_design_id.length > 0 && props._mga_design_id.length <= 128
    && TOKEN.test(props._mga_token || '') && Object.hasOwn(PRINT_SHAPES, props._mga_aspect_ratio || '');
}

function lineCacheKey(line) {
  return JSON.stringify([String(line.variant_id), Object.entries(line.properties).sort(([a], [b]) => a.localeCompare(b))]);
}

/** The print line's properties. Never a design ID or token: registration refuses those on an add-on. */
export function printLineProperties(canvas, identity) {
  const properties = {_mga_addon_kind: 'physical_addon', _mga_addon_offer_id: OFFER_ID, _mga_addon_offer_version: '1',
    _mga_addon_parent_identity: identity.parentIdentity};
  const thumb = String(canvas.properties._mga_thumb_url || canvas.properties._mga_preview_url || '');
  if (/^https:\/\/[^\s"'<>]{1,2000}$/.test(thumb)) properties._mga_print_thumb_url = thumb;
  return properties;
}

export function createPrintController({identify, getCart, addLine, canMutate = () => true, getConfig}) {
  const identities = new Map();
  const blocked = new Set();
  let busy = false;
  async function identityOf(line) {
    const key = lineCacheKey(line);
    if (!identities.has(key)) {
      identities.set(key, identify({parentVariantId: String(line.variant_id), parentProperties: line.properties}).then(value => {
        if (!PARENT_ID.test(value?.parentIdentity || '')) throw new Error('Unavailable');
        return value;
      }).catch(error => { identities.delete(key); throw error; }));
    }
    return identities.get(key);
  }
  const printsFor = (cart, parentIdentity) => (cart?.items || []).filter(line => isPrintLine(line)
    && line.properties?._mga_addon_parent_identity === parentIdentity);
  return {
    identityOf,
    printsFor,
    isBusy: () => busy,
    isBlocked: parentIdentity => blocked.has(parentIdentity),
    /** Prints whose canvas line left the cart (remove) or above MAX_PRINT_QUANTITY copies (set to it).
     * Null when a canvas line's identity is unknown: then nothing is touched. */
    async repairs(cart) {
      const prints = (cart?.items || []).filter(isPrintLine);
      if (!prints.length) return [];
      let parents;
      try { parents = new Set(await Promise.all((cart.items || []).filter(canvasLine).map(async line => (await identityOf(line)).parentIdentity))); }
      catch { return null; }
      const seen = new Set();
      const changes = [];
      for (const line of prints) {
        const parent = line.properties?._mga_addon_parent_identity;
        if (!parents.has(parent) || seen.has(parent)) changes.push({id: line.key, quantity: 0});
        else if (!validPrintQuantity(line.quantity)) changes.push({id: line.key, quantity: MAX_PRINT_QUANTITY});
        seen.add(parent);
      }
      return changes;
    },
    async add(lineKey, expectedIdentity, config, sections = [], quantity = 1) {
      if (busy) return {status: 'blocked'};
      if (!validPrintQuantity(quantity)) return {status: 'unavailable'};
      if (!canMutate()) return {status: 'unavailable'};
      busy = true;
      try {
        config = printConfiguration(getConfig ? await getConfig() : config);
        if (!config) return {status: 'unavailable'};
        const cart = await getCart();
        const matches = (cart?.items || []).filter(line => line.key === lineKey);
        const canvas = matches.length === 1 && canvasLine(matches[0]) ? matches[0] : null;
        if (!canvas) return {status: 'unavailable'};
        const identity = await identityOf(canvas);
        if (identity.parentIdentity !== expectedIdentity) return {status: 'unavailable'};
        const variant = VARIANT.exec(config?.matchingPhotoPrint?.sizes?.[identity.size]?.variantId || '');
        if (!variant || identity.printVariantId !== variant[0]) return {status: 'unavailable'};
        const existing = printsFor(cart, identity.parentIdentity);
        if (existing.length) return existing.length === 1 && validPrintQuantity(existing[0].quantity) && String(existing[0].variant_id) === variant[1]
          ? {status: 'accepted', already: true, cart} : {status: 'unavailable'};
        if (blocked.has(identity.parentIdentity)) return {status: 'blocked'};
        if (!canMutate()) return {status: 'unavailable'};
        blocked.add(identity.parentIdentity);
        let response;
        try {
          response = await addLine({items: [{id: Number(variant[1]), quantity, properties: printLineProperties(canvas, identity)}],
            ...(sections.length ? {sections} : {})});
        } catch { }
        // add.js returns only added items; confirm the complete current cart and parent before acceptance.
        try {
          const after = await getCart();
          const parents = (after?.items || []).filter(line => canvasLine(line) && lineCacheKey(line) === lineCacheKey(canvas));
          const prints = printsFor(after, identity.parentIdentity);
          return parents.length === 1 && prints.length === 1 && prints[0].quantity === quantity && String(prints[0].variant_id) === variant[1]
            ? {status: 'accepted', response, cart: after} : {status: 'uncertain'};
        } catch { return {status: 'uncertain'}; }
      } catch { return {status: 'unavailable'}; }
      finally { busy = false; }
    }
  };
}

export function analyticsAllowed(win) {
  try {
    const privacy = win?.Shopify?.customerPrivacy;
    return win?.MGA_TRACKING_DISABLED !== true && privacy?.analyticsProcessingAllowed?.call(privacy) === true;
  } catch { return false; }
}

async function requestJson(url, options = {}) {
  return requestOfferJson(url, options);
}

function startPrintOffers() {
  const owned = location.protocol === 'https:' && ['myguardianart.com', 'www.myguardianart.com', SHOP].includes(location.hostname);
  if (!owned) return;
  const root = window.Shopify?.routes?.root || '/';
  const getCart = () => requestJson(`${root}cart.js`, {cache: 'no-store', dedupe: !mutation.isBusy()});
  const getConfig = () => requestJson(`${WORKER_URL}/checkout-offers/config?shop=${SHOP}`, {cache: 'no-store'}).then(printConfiguration);
  const post = (path, body) => requestJson(`${root}${path}`, {method: 'POST', headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({...body, sections_url: location.pathname})});
  const mutation = cartOfferMutationLock();
  const controller = createPrintController({
    identify: input => requestJson(`${WORKER_URL}/checkout-offers/parent-identity`, {method: 'POST', credentials: 'omit',
      headers: {'Content-Type': 'application/json'}, body: JSON.stringify(input)}),
    getCart, getConfig, canMutate: () => mutation.isBusy(), addLine: body => post('cart/add.js', body)
  });
  const ready = new WeakMap();
  const viewed = new Set();
  let config = null;
  let refreshing = null;
  let again = false;
  let sessionId;
  let sequence = 0;

  function track(eventName, placement, quantity = 1) {
    if (!analyticsAllowed(window)) return;
    try {
      if (!sessionId) {
        const stored = sessionStorage.getItem('mga_funnel_session_v1:cart');
        sessionId = UUID.test(stored || '') ? stored : crypto.randomUUID();
        sessionStorage.setItem('mga_funnel_session_v1:cart', sessionId);
      }
      const event = {event_id: crypto.randomUUID(), event_name: eventName, occurred_at: Date.now(),
        event_sequence: ++sequence, funnel_session_id: sessionId, page_path: location.pathname,
        properties: {offer_id: OFFER_ID, offer_version: '1', offer_kind: 'physical_addon', offer_placement: placement,
          quantity_before: 0, quantity_after: eventName === 'checkout_offer_accepted' ? quantity : 0,
          analytics_allowed: true, event_source: 'theme', tracking_version: 'funnel-v2'}};
      void fetch(`${WORKER_URL}/track-funnel`, {method: 'POST', headers: {'Content-Type': 'text/plain;charset=UTF-8'},
        body: JSON.stringify({events: [event]}), keepalive: true}).catch(() => {});
    } catch { }
  }

  function hide(card) { card.hidden = true; card.setAttribute('aria-busy', 'false'); ready.delete(card); }

  function reloadAllowed() {
    try {
      const last = Number(sessionStorage.getItem('mga_print_offer_repair_reload') || 0);
      if (Date.now() - last < 30000) return false;
      sessionStorage.setItem('mga_print_offer_repair_reload', String(Date.now()));
      return true;
    } catch { return false; }
  }

  async function refresh() {
    if (mutation.isBusy()) return;
    if (refreshing) { again = true; return refreshing; }
    refreshing = (async () => {
      // An existing print must be reconciled with its parent before checkout resumes.
      // Native updates release into this lock synchronously after their section replacement.
      const repairRelease = document.querySelector('[data-mga-print-line]') ? mutation.acquire() : null;
      if (document.querySelector('[data-mga-print-line]') && !repairRelease) {
        setTimeout(() => void refresh(), 1000); return;
      }
      // Liquid supplies the exact cart line so the first identity read can start immediately,
      // alongside configuration/cart reads, rather than after two network round trips.
      for (const card of document.querySelectorAll(SELECTOR)) {
        try {
          const line = JSON.parse(card.dataset.parentLine || 'null');
          if (line?.properties && line.variant_id) void controller.identityOf(line).catch(() => {});
        } catch { }
      }
      let cards = [];
      try {
        let cart;
        [cart, config] = await Promise.all([getCart(), getConfig().catch(() => null)]);
        if (mutation.isBusy() && !repairRelease) return;
        cards = [...document.querySelectorAll(SELECTOR)];
        if (config) {
          const changes = await controller.repairs(cart);
          let changed = 0;
          if (changes?.length) {
            const release = repairRelease || mutation.acquire();
            if (!release) { setTimeout(() => void refresh(), 1000); return; }
            try {
              // Repairs use a fresh cart inside the same lock as both optional additions.
              const fresh = await getCart();
              for (const change of await controller.repairs(fresh) || []) {
                try { await post('cart/change.js', {...change, sections: ['cart-icon-bubble']}); changed++; } catch { }
              }
              cart = await getCart();
            } finally { if (!repairRelease) release(); }
          }
          // The cart page shows lines and totals from the server: reload it after a repair,
          // at most once in 30 seconds so a failing repair can never loop.
          if (changed && document.querySelector('cart-items') && reloadAllowed()) { location.reload(); return; }
        }
        await Promise.all(cards.map(async card => {
          if (!card.isConnected) return;
          const matches = (cart.items || []).filter(line => line.key === card.dataset.lineKey);
          const line = matches.length === 1 ? matches[0] : null;
          if (!config || !line || !canvasLine(line)) { hide(card); return; }
          let identity;
          try { identity = await controller.identityOf(line); } catch { hide(card); return; }
          if (!card.isConnected || (mutation.isBusy() && !repairRelease)) return;
          if (!identity.size || !config.matchingPhotoPrint.sizes[identity.size]
            || identity.printVariantId !== `gid://shopify/ProductVariant/${card.dataset.printVariantId}`
            || controller.printsFor(cart, identity.parentIdentity).length) {
            if (!card.querySelector('.mga-print-offer__status--added')) hide(card);
            return;
          }
          ready.set(card, identity);
          const button = card.querySelector('[data-mga-print-add]');
          button.disabled = controller.isBusy() || controller.isBlocked(identity.parentIdentity);
          if (card.getAttribute('aria-busy') === 'true') {
            card.setAttribute('aria-busy', 'false');
            button.textContent = labelFor(card) || button.textContent;
            card.querySelector('.mga-print-offer__status').hidden = true;
          }
          card.hidden = false;
          if (!viewed.has(identity.parentIdentity)) observeOfferExposure(card, () => {
            const visible = !card.closest('cart-notification') || card.closest('#cart-notification')?.classList.contains('active');
            if (!visible || !analyticsAllowed(window)) return false;
            if (!viewed.has(identity.parentIdentity)) { viewed.add(identity.parentIdentity); track('checkout_offer_viewed', card.dataset.placement); }
            return true;
          });
        }));
      } catch { for (const card of document.querySelectorAll(SELECTOR)) hide(card); }
      finally { repairRelease?.({notify: false}); }
    })().finally(() => {
      refreshing = null;
      if (again) { again = false; setTimeout(() => void refresh(), 100); }
    });
    return refreshing;
  }

  function renderCartPage(response) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || !response?.sections) return false;
    let rendered = true;
    for (const section of cartItems.getSectionsToRender()) {
      const html = response.sections[section.section];
      const target = document.getElementById(section.id);
      const replacement = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (!target || !replacement) { rendered = false; continue; }
      (target.querySelector(section.selector) || target).innerHTML = replacement.innerHTML;
    }
    cartItems.currentItemCount = [...cartItems.querySelectorAll('[name="updates[]"]')].reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);
    return rendered;
  }

  function renderBubble(response) {
    const html = response?.sections?.['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    const replacement = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (target && replacement) target.innerHTML = replacement.innerHTML;
  }

  function selectedQuantity(card) {
    const value = Number(card.querySelector('[data-mga-print-quantity]')?.value || 1);
    return validPrintQuantity(value) ? value : 1;
  }

  // The button names the total for more than one copy: "Add 3 photo prints · $29.97".
  function labelFor(card) {
    const quantity = selectedQuantity(card);
    const cents = Number(card.dataset.priceCents);
    if (quantity === 1 || !Number.isFinite(cents) || !card.dataset.addMany) return card.dataset.readyLabel;
    const total = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(cents * quantity / 100);
    return card.dataset.addMany.replace('%COUNT%', String(quantity)).replace('%PRICE%', total);
  }

  document.addEventListener('change', event => {
    const select = event.target.closest?.('[data-mga-print-quantity]');
    const card = select?.closest(SELECTOR);
    const button = card?.querySelector('[data-mga-print-add]');
    if (button && card.getAttribute('aria-busy') !== 'true') button.textContent = labelFor(card);
  });

  function showStatus(card, text, added) {
    const status = card.querySelector('.mga-print-offer__status');
    status.textContent = text;
    status.classList.toggle('mga-print-offer__status--added', added);
    status.hidden = false;
  }

  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-mga-print-add]');
    if (!button) return;
    event.preventDefault();
    const card = button.closest(SELECTOR);
    const identity = ready.get(card);
    if (!identity || button.disabled || !config) return;
    const release = mutation.acquire();
    if (!release) return;
    const quantity = selectedQuantity(card);
    track('checkout_offer_clicked', card.dataset.placement);
    const cartItems = document.querySelector('cart-items');
    const sections = cartItems ? [...new Set(cartItems.getSectionsToRender().map(section => section.section))] : ['cart-icon-bubble'];
    let result;
    let text;
    try {
      result = await controller.add(card.dataset.lineKey, identity.parentIdentity, config, sections, quantity);
      if (result.status !== 'blocked') {
        text = card.dataset[result.status === 'accepted' ? 'added' : result.status === 'uncertain' ? 'uncertain' : 'unavailable'];
        track(result.status === 'accepted' ? 'checkout_offer_accepted' : 'checkout_offer_failed', card.dataset.placement, quantity);
        announceOffer(text);
      }
      if (result.status === 'accepted' && cartItems) {
        if (result.already || !renderCartPage(result.response)) { location.reload(); return; }
      }
    } finally { release(); }
    if (result.status === 'blocked') { await refresh(); return; }
    if (result.status !== 'accepted') {
      button.disabled = controller.isBlocked(identity.parentIdentity);
      showStatus(card, text, false);
      const status = card.querySelector('.mga-print-offer__status');
      status.tabIndex = -1; status.focus();
      return;
    }
    button.hidden = true;
    const choice = card.querySelector('.mga-print-offer__quantity');
    if (choice) choice.hidden = true;
    showStatus(card, text, true);
    if (cartItems) {
      const printIndex = result.cart.items.findIndex(line => isPrintLine(line) && line.properties?._mga_addon_parent_identity === identity.parentIdentity);
      const row = document.getElementById(`CartItem-${printIndex + 1}`);
      if (row) {
        const status = document.createElement('p'); status.className = 'mga-print-offer__status mga-print-offer__status--added';
        status.textContent = text; status.tabIndex = -1;
        (row.querySelector('.cart-item__details') || row).append(status); status.focus();
      } else document.getElementById('checkout')?.focus();
      await refresh();
      return;
    }
    renderBubble(result.response);
  });

  // New cards, and new cart rows: a canvas removed on the cart page must take its print with it.
  const WATCHED = `${SELECTOR}, .cart-item`;
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1
      && (node.matches?.(WATCHED) || node.querySelector?.(WATCHED))))) setTimeout(() => void refresh(), 100);
  }).observe(document.body, {childList: true, subtree: true});
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void refresh(); });
  document.addEventListener('visitorConsentCollected', () => void refresh());
  document.addEventListener('mga:cart-offers:changed', () => void refresh());
  window.addEventListener('pageshow', () => void refresh());
  if (document.querySelector(SELECTOR) || location.pathname.replace(/\/$/, '').endsWith('/cart')) void refresh();
}

if (typeof document !== 'undefined') startPrintOffers();
